import ProductViewerModal from '@c/modals/ProductViewerModal';
import ProductCarousel from '@ui/ProductCarousel';
import { useWarehouse } from 'app/product/[slug]/components/WarehouseProvider';
import { ProductDocument, Variation } from 'models/product';
import Image from 'next/image';
import { memo, useMemo, useState } from 'react';

const ProductDetailsImages = ({
  product,
  selectedVariations,
}: {
  product: ProductDocument;
  selectedVariations: Variation[] | null;
}) => {
  const { relatedItems, fittingItems } = useWarehouse();
  const [openGalleryModal, setOpenGalleryModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(
    product.video ? -1 : 0
  );

  const galleryProduct = useMemo(() => {
    if (!product?.images) return product;

    // Warehouse fitment catalogs (grips, tires and vehicle-specific parts) list
    // one image per color across the group; other products keep their own
    // images.
    const isFitmentGroup =
      relatedItems.length > 0 && !!product.fitting_bikes?.bikes?.length;
    if (!isFitmentGroup) return product;

    const items = [product, ...fittingItems];

    const seenColors = new Set<string>();
    const seenImages = new Set<string>();
    const colorImages: ProductDocument['images'] = [];
    for (const item of items) {
      const image = item.images?.[0];
      const color = item.color?.trim().toLowerCase();
      if (!image || !color || color === 'n/a' || seenColors.has(color)) continue;
      seenColors.add(color);
      const key = image.full || image.thumb;
      if (!key || seenImages.has(key)) continue;
      seenImages.add(key);
      colorImages.push(image);
    }

    // Each color variation contributes a single image; colors that share one
    // photo collapse, so finish labels backed by the same image are not
    // treated as variations.
    if (colorImages.length > 1) {
      return { ...product, images: colorImages };
    }

    // No real color variation → only the cover photo.
    return { ...product, images: product.images.slice(0, 1) };
  }, [relatedItems, product, fittingItems]);

  if (!product?.images)
    return (
      <Image
        alt="No Images Found"
        width={500}
        height={500}
        src="/not_found.svg"
      />
    );

  const handleImageClick = (index: number) => {
    setCurrentImageIndex(index);
    setOpenGalleryModal(true);
  };

  return (
    <div className="relative grow sm:max-w-fit lg:mt-[1rem]">
      <ProductCarousel
        product={galleryProduct}
        openProductViewer={handleImageClick}
        selectedVariations={selectedVariations}
        stack
      />

      {/* Gallery Modal - Opens directly to fullscreen viewer */}
      {openGalleryModal && (
        <ProductViewerModal
          isOpen={openGalleryModal}
          dismiss={() => setOpenGalleryModal(false)}
          images={galleryProduct.images}
          video={galleryProduct.video}
          initialImage={currentImageIndex}
        />
      )}
    </div>
  );
};

// Memoize this component to prevent unnecessary re-renders and image flickers
export default memo(ProductDetailsImages);
